import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  LayoutDashboard, 
  Users, 
  ArrowLeftRight, 
  Settings,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Wallet,
  UserCheck,
  Activity,
  ChevronRight,
  BarChart3,
  AlertTriangle,
  Zap,
  Shield,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Search,
  ChevronDown,
  Edit3,
  Save
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sberbank color scheme
const SBER = {
  green: '#21A038',
  greenDark: '#1A8F2D',
  greenLight: '#4DB45F',
  greenPale: '#E8F5E9',
  white: '#FFFFFF',
  bg: '#F5F5F5',
  dark: '#1A1A1A',
  gray: '#6B7280',
  grayLight: '#9CA3AF',
  grayPale: '#F3F4F6',
};

// Animation variants
const pageVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.2 } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.15 } }
};

const tapAnimation = { scale: 0.97 };

// Card Component
const AppCard = ({ children, className = "", onClick, accent = false }) => (
  <motion.div
    whileTap={onClick ? tapAnimation : undefined}
    onClick={onClick}
    className={`rounded-2xl transition-all ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''} ${className}`}
    style={{ 
      background: accent ? `linear-gradient(135deg, ${SBER.green} 0%, ${SBER.greenDark} 100%)` : SBER.white,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    }}
  >
    {children}
  </motion.div>
);

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue }) => (
  <AppCard className="p-4">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-[12px] font-medium uppercase tracking-wide" style={{ color: SBER.gray }}>
          {title}
        </p>
        <p className="text-[24px] font-bold mt-1" style={{ color: SBER.dark, fontFamily: "'JetBrains Mono', monospace" }}>
          {value}
        </p>
        {subtitle && (
          <div className="flex items-center gap-1 mt-1">
            {trend === 'up' && <ArrowUpRight className="w-3 h-3 text-emerald-500" />}
            {trend === 'down' && <ArrowDownRight className="w-3 h-3 text-red-500" />}
            <span className={`text-[12px] ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : ''}`} style={{ color: trend ? undefined : SBER.grayLight }}>
              {subtitle}
            </span>
          </div>
        )}
      </div>
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: SBER.greenPale }}
      >
        <Icon className="w-5 h-5" style={{ color: SBER.green }} />
      </div>
    </div>
  </AppCard>
);

// Progress Bar Component
const ProgressBar = ({ value, max, label, color = SBER.green }) => {
  const percent = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[12px]">
        <span style={{ color: SBER.gray }}>{label}</span>
        <span className="font-medium" style={{ color: SBER.dark }}>{value} / {max}</span>
      </div>
      <div className="h-2 rounded-full" style={{ background: SBER.grayPale }}>
        <div 
          className="h-2 rounded-full transition-all"
          style={{ width: `${percent}%`, background: color }}
        />
      </div>
    </div>
  );
};

// Bottom Navigation
const BottomNav = ({ active, onNavigate }) => {
  const items = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
    { id: 'users', icon: Users, label: 'Пользователи' },
    { id: 'transactions', icon: ArrowLeftRight, label: 'Транзакции' },
    { id: 'settings', icon: Settings, label: 'Настройки' },
  ];

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 px-4 py-2 flex justify-around items-center"
      style={{ 
        background: SBER.white, 
        borderTop: `1px solid ${SBER.grayPale}`,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.05)'
      }}
    >
      {items.map((item) => (
        <motion.button
          key={item.id}
          whileTap={tapAnimation}
          onClick={() => onNavigate(item.id)}
          className="flex flex-col items-center py-2 px-4"
        >
          <item.icon 
            className="w-5 h-5 mb-1" 
            style={{ color: active === item.id ? SBER.green : SBER.grayLight }}
            strokeWidth={active === item.id ? 2.5 : 2}
          />
          <span 
            className="text-[10px] font-medium"
            style={{ color: active === item.id ? SBER.green : SBER.grayLight }}
          >
            {item.label}
          </span>
        </motion.button>
      ))}
    </div>
  );
};

// Dashboard Page
const DashboardPage = ({ analytics, rate, onRefresh, loading }) => {
  const formatCurrency = (value, currency = "₽") => {
    return new Intl.NumberFormat("ru-RU").format(value) + " " + currency;
  };

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-24"
      style={{ background: SBER.bg }}
    >
      {/* Header */}
      <div 
        className="px-5 pt-6 pb-4"
        style={{ background: SBER.white }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[22px] font-bold" style={{ color: SBER.dark }}>
              Админ-панель
            </h1>
            <p className="text-[13px]" style={{ color: SBER.grayLight }}>
              PolyTech Wallet • Мониторинг
            </p>
          </div>
          <motion.button
            whileTap={tapAnimation}
            onClick={onRefresh}
            disabled={loading}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: SBER.greenPale }}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} style={{ color: SBER.green }} />
          </motion.button>
        </div>
      </div>

      <div className="px-5 pt-5 space-y-4">
        {/* Main Stats */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard 
            title="Пользователи"
            value={analytics?.users?.total || 0}
            subtitle={`+${analytics?.users?.new_today || 0} сегодня`}
            icon={Users}
            trend={analytics?.users?.growth_percent >= 0 ? 'up' : 'down'}
          />
          <StatCard 
            title="Баланс"
            value={`${(analytics?.balance?.total_usdt || 0).toFixed(2)}`}
            subtitle="USDT"
            icon={Wallet}
          />
        </div>

        {/* Volume Card */}
        <AppCard className="p-4" accent>
          <p className="text-[12px] font-medium text-white/70 uppercase tracking-wide">
            Оборот сегодня
          </p>
          <p className="text-[28px] font-bold text-white mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {formatCurrency(analytics?.volume?.today_topup_rub || 0)}
          </p>
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-white/70" />
              <span className="text-[13px] text-white/90">
                {formatCurrency(analytics?.volume?.today_topup_rub || 0)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-white/70" />
              <span className="text-[13px] text-white/90">
                {formatCurrency(analytics?.volume?.today_withdraw_rub || 0)}
              </span>
            </div>
          </div>
        </AppCard>

        {/* Rate Card */}
        <AppCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[12px] font-medium uppercase tracking-wide" style={{ color: SBER.gray }}>
                Текущий курс
              </p>
              <p className="text-[24px] font-bold mt-1" style={{ color: SBER.green, fontFamily: "'JetBrains Mono', monospace" }}>
                1 USDT = {rate?.usdt_rub_rate || 95} ₽
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px]" style={{ color: SBER.grayLight }}>Комиссии</p>
              <p className="text-[13px] font-medium" style={{ color: SBER.dark }}>
                {rate?.buy_fee_percent || 1.7}% / {rate?.sell_fee_percent || 2.0}%
              </p>
            </div>
          </div>
        </AppCard>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <AppCard className="p-3 text-center">
            <UserCheck className="w-5 h-5 mx-auto mb-1" style={{ color: SBER.green }} />
            <p className="text-[16px] font-bold" style={{ color: SBER.dark }}>
              {analytics?.users?.kyc_verified || 0}
            </p>
            <p className="text-[10px]" style={{ color: SBER.grayLight }}>KYC</p>
          </AppCard>
          <AppCard className="p-3 text-center">
            <Activity className="w-5 h-5 mx-auto mb-1" style={{ color: SBER.green }} />
            <p className="text-[16px] font-bold" style={{ color: SBER.dark }}>
              {analytics?.transactions?.today_count || 0}
            </p>
            <p className="text-[10px]" style={{ color: SBER.grayLight }}>Транзакций</p>
          </AppCard>
          <AppCard className="p-3 text-center">
            <Shield className="w-5 h-5 mx-auto mb-1" style={{ color: SBER.green }} />
            <p className="text-[16px] font-bold" style={{ color: SBER.dark }}>
              {analytics?.users?.with_pin || 0}
            </p>
            <p className="text-[10px]" style={{ color: SBER.grayLight }}>С PIN</p>
          </AppCard>
        </div>

        {/* Transactions Status */}
        <AppCard className="p-4">
          <p className="text-[13px] font-semibold mb-3" style={{ color: SBER.dark }}>
            Статус транзакций
          </p>
          <div className="space-y-2">
            {analytics?.transactions?.pending_count > 0 && (
              <div className="flex items-center justify-between p-2 rounded-lg" style={{ background: '#FEF3C7' }}>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-[13px] text-amber-800">В обработке</span>
                </div>
                <span className="text-[14px] font-semibold text-amber-800">
                  {analytics.transactions.pending_count}
                </span>
              </div>
            )}
            {analytics?.transactions?.failed_today > 0 && (
              <div className="flex items-center justify-between p-2 rounded-lg" style={{ background: '#FEE2E2' }}>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-[13px] text-red-800">Ошибки сегодня</span>
                </div>
                <span className="text-[14px] font-semibold text-red-800">
                  {analytics.transactions.failed_today}
                </span>
              </div>
            )}
            {(!analytics?.transactions?.pending_count && !analytics?.transactions?.failed_today) && (
              <div className="flex items-center justify-center p-3 rounded-lg" style={{ background: SBER.greenPale }}>
                <CheckCircle className="w-4 h-4 mr-2" style={{ color: SBER.green }} />
                <span className="text-[13px]" style={{ color: SBER.green }}>Всё в порядке</span>
              </div>
            )}
          </div>
        </AppCard>

        {/* Weekly Chart */}
        <AppCard className="p-4">
          <p className="text-[13px] font-semibold mb-3" style={{ color: SBER.dark }}>
            Объём за неделю
          </p>
          <div className="h-24 flex items-end gap-1">
            {(analytics?.chart_data?.daily_volume || []).map((day, idx) => {
              const maxVal = Math.max(
                ...((analytics?.chart_data?.daily_volume || []).map(d => d.topup + d.withdraw)),
                1
              );
              const height = ((day.topup + day.withdraw) / maxVal) * 100;
              const dayName = new Date(day._id).toLocaleDateString('ru-RU', { weekday: 'short' });
              
              return (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full rounded-t transition-all"
                    style={{ 
                      height: `${Math.max(height, 5)}%`, 
                      background: SBER.green,
                      minHeight: '4px'
                    }}
                  />
                  <span className="text-[9px] mt-1" style={{ color: SBER.grayLight }}>
                    {dayName}
                  </span>
                </div>
              );
            })}
            {(!analytics?.chart_data?.daily_volume || analytics.chart_data.daily_volume.length === 0) && (
              <div className="flex-1 flex items-center justify-center text-[12px]" style={{ color: SBER.grayLight }}>
                Нет данных
              </div>
            )}
          </div>
        </AppCard>
      </div>
    </motion.div>
  );
};

// User Detail Modal
const UserDetailModal = ({ user, onClose, transactions }) => {
  if (!user) return null;
  
  const kycConfig = {
    NOT_STARTED: { label: "Не пройдена", color: SBER.gray, bg: SBER.grayPale },
    PENDING: { label: "На проверке", color: "#F59E0B", bg: "#FEF3C7" },
    APPROVED: { label: "Пройдена", color: SBER.green, bg: SBER.greenPale },
    REJECTED: { label: "Отклонена", color: "#EF4444", bg: "#FEE2E2" }
  };
  const kyc = kycConfig[user.kyc_status] || kycConfig.NOT_STARTED;
  
  const userTxs = transactions.filter(tx => tx.telegram_id === user.telegram_id);
  const totalTopup = userTxs.filter(tx => tx.type === 'TOPUP_RUB_SBP' && tx.status === 'COMPLETED')
    .reduce((sum, tx) => sum + (tx.amount_rub || 0), 0);
  const totalWithdraw = userTxs.filter(tx => ['PAYOUT_RUB_TO_CARD', 'PAYOUT_SBP'].includes(tx.type) && tx.status === 'COMPLETED')
    .reduce((sum, tx) => sum + (tx.amount_rub || 0), 0);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-[430px] max-h-[85vh] rounded-t-3xl overflow-hidden"
        style={{ background: SBER.white }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b" style={{ borderColor: SBER.grayPale }}>
          <h2 className="text-[18px] font-bold" style={{ color: SBER.dark }}>Профиль пользователя</h2>
          <motion.button
            whileTap={tapAnimation}
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: SBER.grayPale }}
          >
            <XCircle className="w-5 h-5" style={{ color: SBER.gray }} />
          </motion.button>
        </div>
        
        <div className="px-5 py-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 60px)' }}>
          {/* User Info */}
          <div className="flex items-center gap-4 mb-6">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold"
              style={{ background: SBER.green }}
            >
              {(user.first_name?.[0] || 'U').toUpperCase()}
            </div>
            <div>
              <p className="text-[18px] font-bold" style={{ color: SBER.dark }}>
                {user.first_name || 'Unknown'}
              </p>
              <p className="text-[14px]" style={{ color: SBER.green }}>
                {user.username ? `@${user.username}` : 'Без username'}
              </p>
              <p className="text-[12px]" style={{ color: SBER.grayLight }}>
                ID: {user.telegram_id}
              </p>
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <AppCard className="p-4 text-center">
              <p className="text-[10px] uppercase" style={{ color: SBER.grayLight }}>Баланс</p>
              <p className="text-[20px] font-bold" style={{ color: SBER.green, fontFamily: "'JetBrains Mono', monospace" }}>
                {(user.balance_usdt || 0).toFixed(2)}
              </p>
              <p className="text-[11px]" style={{ color: SBER.grayLight }}>USDT</p>
            </AppCard>
            <AppCard className="p-4 text-center">
              <p className="text-[10px] uppercase" style={{ color: SBER.grayLight }}>Транзакций</p>
              <p className="text-[20px] font-bold" style={{ color: SBER.dark }}>
                {userTxs.length}
              </p>
              <p className="text-[11px]" style={{ color: SBER.grayLight }}>всего</p>
            </AppCard>
          </div>
          
          {/* Financial Stats */}
          <AppCard className="p-4 mb-4">
            <p className="text-[13px] font-semibold mb-3" style={{ color: SBER.dark }}>
              Финансовые показатели
            </p>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-[13px]" style={{ color: SBER.gray }}>Всего пополнений</span>
                <span className="font-semibold text-emerald-600">{totalTopup.toLocaleString()} ₽</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[13px]" style={{ color: SBER.gray }}>Всего выводов</span>
                <span className="font-semibold text-amber-600">{totalWithdraw.toLocaleString()} ₽</span>
              </div>
              <div className="h-px" style={{ background: SBER.grayPale }} />
              <div className="flex justify-between">
                <span className="text-[13px] font-medium" style={{ color: SBER.dark }}>Нетто</span>
                <span className={`font-bold ${totalTopup - totalWithdraw >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totalTopup - totalWithdraw >= 0 ? '+' : ''}{(totalTopup - totalWithdraw).toLocaleString()} ₽
                </span>
              </div>
            </div>
          </AppCard>
          
          {/* Status Info */}
          <AppCard className="p-4 mb-4">
            <p className="text-[13px] font-semibold mb-3" style={{ color: SBER.dark }}>
              Статус
            </p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[13px]" style={{ color: SBER.gray }}>KYC верификация</span>
                <span 
                  className="px-3 py-1 rounded-full text-[12px] font-medium"
                  style={{ background: kyc.bg, color: kyc.color }}
                >
                  {kyc.label}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[13px]" style={{ color: SBER.gray }}>PIN-код</span>
                <span 
                  className="px-3 py-1 rounded-full text-[12px] font-medium"
                  style={{ 
                    background: user.has_pin ? SBER.greenPale : SBER.grayPale,
                    color: user.has_pin ? SBER.green : SBER.gray
                  }}
                >
                  {user.has_pin ? 'Установлен' : 'Не установлен'}
                </span>
              </div>
            </div>
          </AppCard>
          
          {/* Registration Info */}
          <AppCard className="p-4">
            <p className="text-[13px] font-semibold mb-3" style={{ color: SBER.dark }}>
              Даты
            </p>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-[13px]" style={{ color: SBER.gray }}>Регистрация</span>
                <span className="text-[13px] font-medium" style={{ color: SBER.dark }}>
                  {user.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  }) : 'Н/Д'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[13px]" style={{ color: SBER.gray }}>Последняя активность</span>
                <span className="text-[13px] font-medium" style={{ color: SBER.dark }}>
                  {userTxs.length > 0 
                    ? new Date(userTxs[0].created_at).toLocaleDateString('ru-RU', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                      })
                    : 'Нет транзакций'
                  }
                </span>
              </div>
            </div>
          </AppCard>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Users Page with filters
const UsersPage = ({ onBack }) => {
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [sortBy, setSortBy] = useState('created_at'); // created_at, balance, transactions
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [kycFilter, setKycFilter] = useState('all'); // all, APPROVED, PENDING, NOT_STARTED, REJECTED
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [balanceFilter, setBalanceFilter] = useState('all'); // all, zero, low, medium, high

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, txRes] = await Promise.all([
          axios.get(`${API}/users?limit=100`),
          axios.get(`${API}/transactions?limit=500`)
        ]);
        setUsers(usersRes.data);
        setTransactions(txRes.data);
      } catch (e) {
        toast.error("Ошибка загрузки");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Calculate user stats
  const getUserStats = (user) => {
    const userTxs = transactions.filter(tx => tx.telegram_id === user.telegram_id);
    const completedTxs = userTxs.filter(tx => tx.status === 'COMPLETED');
    const totalVolume = completedTxs.reduce((sum, tx) => sum + (tx.amount_rub || 0), 0);
    return {
      txCount: userTxs.length,
      completedCount: completedTxs.length,
      totalVolume
    };
  };

  // Filter and sort users
  const filteredUsers = users
    .filter(u => {
      // Search filter
      const searchMatch = 
        (u.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.first_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(u.telegram_id).includes(searchQuery);
      
      if (!searchMatch) return false;
      
      // KYC filter
      if (kycFilter !== 'all' && u.kyc_status !== kycFilter) return false;
      
      // Date filter
      if (dateFilter !== 'all') {
        const regDate = new Date(u.created_at);
        const now = new Date();
        const daysDiff = (now - regDate) / (1000 * 60 * 60 * 24);
        
        if (dateFilter === 'today' && daysDiff > 1) return false;
        if (dateFilter === 'week' && daysDiff > 7) return false;
        if (dateFilter === 'month' && daysDiff > 30) return false;
      }
      
      // Balance filter
      const balance = u.balance_usdt || 0;
      if (balanceFilter === 'zero' && balance > 0) return false;
      if (balanceFilter === 'low' && (balance <= 0 || balance > 100)) return false;
      if (balanceFilter === 'medium' && (balance <= 100 || balance > 1000)) return false;
      if (balanceFilter === 'high' && balance <= 1000) return false;
      
      return true;
    })
    .map(u => ({ ...u, stats: getUserStats(u) }))
    .sort((a, b) => {
      let valA, valB;
      
      switch (sortBy) {
        case 'balance':
          valA = a.balance_usdt || 0;
          valB = b.balance_usdt || 0;
          break;
        case 'transactions':
          valA = a.stats.txCount;
          valB = b.stats.txCount;
          break;
        case 'volume':
          valA = a.stats.totalVolume;
          valB = b.stats.totalVolume;
          break;
        default:
          valA = new Date(a.created_at || 0).getTime();
          valB = new Date(b.created_at || 0).getTime();
      }
      
      return sortOrder === 'desc' ? valB - valA : valA - valB;
    });

  const kycConfig = {
    NOT_STARTED: { label: "Не пройдена", color: SBER.gray, bg: SBER.grayPale },
    PENDING: { label: "На проверке", color: "#F59E0B", bg: "#FEF3C7" },
    APPROVED: { label: "Пройдена", color: SBER.green, bg: SBER.greenPale },
    REJECTED: { label: "Отклонена", color: "#EF4444", bg: "#FEE2E2" }
  };

  // Filter Chip Component
  const FilterChip = ({ label, active, onClick }) => (
    <motion.button
      whileTap={tapAnimation}
      onClick={onClick}
      className="px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-all"
      style={{
        background: active ? SBER.green : SBER.white,
        color: active ? SBER.white : SBER.gray,
        border: `1px solid ${active ? SBER.green : SBER.grayPale}`
      }}
    >
      {label}
    </motion.button>
  );

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-24"
      style={{ background: SBER.bg }}
    >
      {/* Header */}
      <div className="px-5 pt-6 pb-4" style={{ background: SBER.white }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[22px] font-bold" style={{ color: SBER.dark }}>
              Пользователи
            </h1>
            <p className="text-[13px]" style={{ color: SBER.grayLight }}>
              Найдено: {filteredUsers.length} из {users.length}
            </p>
          </div>
          <motion.button
            whileTap={tapAnimation}
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-2 rounded-xl flex items-center gap-2"
            style={{ 
              background: showFilters ? SBER.green : SBER.grayPale,
              color: showFilters ? SBER.white : SBER.gray
            }}
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            <span className="text-[13px] font-medium">Фильтры</span>
          </motion.button>
        </div>
      </div>

      <div className="px-5 pt-4 space-y-4">
        {/* Search */}
        <AppCard className="p-3">
          <div className="flex items-center gap-3">
            <Search className="w-5 h-5" style={{ color: SBER.grayLight }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по имени или ID"
              className="flex-1 bg-transparent outline-none text-[14px]"
              style={{ color: SBER.dark }}
            />
          </div>
        </AppCard>
        
        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <AppCard className="p-4 space-y-4">
                {/* Sort */}
                <div>
                  <p className="text-[11px] font-semibold uppercase mb-2" style={{ color: SBER.gray }}>
                    Сортировка
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <FilterChip label="По дате" active={sortBy === 'created_at'} onClick={() => setSortBy('created_at')} />
                    <FilterChip label="По балансу" active={sortBy === 'balance'} onClick={() => setSortBy('balance')} />
                    <FilterChip label="По транзакциям" active={sortBy === 'transactions'} onClick={() => setSortBy('transactions')} />
                    <FilterChip label="По объёму" active={sortBy === 'volume'} onClick={() => setSortBy('volume')} />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <FilterChip 
                      label={sortOrder === 'desc' ? '↓ Убывание' : '↑ Возрастание'} 
                      active={true} 
                      onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')} 
                    />
                  </div>
                </div>
                
                {/* Date Filter */}
                <div>
                  <p className="text-[11px] font-semibold uppercase mb-2" style={{ color: SBER.gray }}>
                    Дата регистрации
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <FilterChip label="Все" active={dateFilter === 'all'} onClick={() => setDateFilter('all')} />
                    <FilterChip label="Сегодня" active={dateFilter === 'today'} onClick={() => setDateFilter('today')} />
                    <FilterChip label="Неделя" active={dateFilter === 'week'} onClick={() => setDateFilter('week')} />
                    <FilterChip label="Месяц" active={dateFilter === 'month'} onClick={() => setDateFilter('month')} />
                  </div>
                </div>
                
                {/* KYC Filter */}
                <div>
                  <p className="text-[11px] font-semibold uppercase mb-2" style={{ color: SBER.gray }}>
                    KYC статус
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <FilterChip label="Все" active={kycFilter === 'all'} onClick={() => setKycFilter('all')} />
                    <FilterChip label="Пройдена" active={kycFilter === 'APPROVED'} onClick={() => setKycFilter('APPROVED')} />
                    <FilterChip label="На проверке" active={kycFilter === 'PENDING'} onClick={() => setKycFilter('PENDING')} />
                    <FilterChip label="Не пройдена" active={kycFilter === 'NOT_STARTED'} onClick={() => setKycFilter('NOT_STARTED')} />
                  </div>
                </div>
                
                {/* Balance Filter */}
                <div>
                  <p className="text-[11px] font-semibold uppercase mb-2" style={{ color: SBER.gray }}>
                    Баланс USDT
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <FilterChip label="Все" active={balanceFilter === 'all'} onClick={() => setBalanceFilter('all')} />
                    <FilterChip label="Нулевой" active={balanceFilter === 'zero'} onClick={() => setBalanceFilter('zero')} />
                    <FilterChip label="0-100" active={balanceFilter === 'low'} onClick={() => setBalanceFilter('low')} />
                    <FilterChip label="100-1000" active={balanceFilter === 'medium'} onClick={() => setBalanceFilter('medium')} />
                    <FilterChip label=">1000" active={balanceFilter === 'high'} onClick={() => setBalanceFilter('high')} />
                  </div>
                </div>
                
                {/* Reset Filters */}
                <motion.button
                  whileTap={tapAnimation}
                  onClick={() => {
                    setSortBy('created_at');
                    setSortOrder('desc');
                    setKycFilter('all');
                    setDateFilter('all');
                    setBalanceFilter('all');
                  }}
                  className="w-full py-2 rounded-xl text-[13px] font-medium"
                  style={{ background: SBER.grayPale, color: SBER.gray }}
                >
                  Сбросить фильтры
                </motion.button>
              </AppCard>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Users List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" style={{ color: SBER.green }} />
          </div>
        ) : (
          <div className="space-y-2">
            {filteredUsers.map((user) => {
              const kyc = kycConfig[user.kyc_status] || kycConfig.NOT_STARTED;
              return (
                <AppCard key={user.telegram_id} className="p-4" onClick={() => setSelectedUser(user)}>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                      style={{ background: SBER.green }}
                    >
                      {(user.first_name?.[0] || 'U').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" style={{ color: SBER.dark }}>
                        {user.first_name || 'Unknown'}
                      </p>
                      <p className="text-[12px]" style={{ color: SBER.grayLight }}>
                        {user.username ? `@${user.username}` : `ID: ${user.telegram_id}`}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px]" style={{ color: SBER.grayLight }}>
                          📋 {user.stats?.txCount || 0} тр.
                        </span>
                        <span className="text-[10px]" style={{ color: SBER.grayLight }}>
                          💰 {(user.stats?.totalVolume || 0).toLocaleString()} ₽
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[14px]" style={{ color: SBER.dark, fontFamily: "'JetBrains Mono', monospace" }}>
                        {(user.balance_usdt || 0).toFixed(2)}
                      </p>
                      <span 
                        className="text-[10px] px-2 py-0.5 rounded-full"
                        style={{ background: kyc.bg, color: kyc.color }}
                      >
                        {kyc.label}
                      </span>
                      <p className="text-[9px] mt-1" style={{ color: SBER.grayLight }}>
                        {new Date(user.created_at).toLocaleDateString('ru-RU')}
                      </p>
                    </div>
                  </div>
                </AppCard>
              );
            })}
            {filteredUsers.length === 0 && (
              <div className="text-center py-8" style={{ color: SBER.grayLight }}>
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Пользователи не найдены</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* User Detail Modal */}
      <AnimatePresence>
        {selectedUser && (
          <UserDetailModal 
            user={selectedUser} 
            transactions={transactions}
            onClose={() => setSelectedUser(null)} 
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Transactions Page
const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const res = await axios.get(`${API}/transactions?limit=50`);
        setTransactions(res.data);
      } catch (e) {
        toast.error("Ошибка загрузки");
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  const typeNames = {
    TOPUP_RUB_SBP: { label: "Пополнение", icon: TrendingUp, color: SBER.green },
    TOPUP_CARD: { label: "Visa/MC", icon: TrendingUp, color: SBER.green },
    TOPUP_MIA: { label: "MIA QR", icon: TrendingUp, color: SBER.green },
    PAYOUT_RUB_TO_CARD: { label: "На карту", icon: TrendingDown, color: "#F59E0B" },
    PAYOUT_CARD: { label: "Visa/MC", icon: TrendingDown, color: "#F59E0B" },
    EXCHANGE_RUB_TO_USDT: { label: "Обмен", icon: ArrowLeftRight, color: "#3B82F6" },
    EXCHANGE_USDT_TO_RUB: { label: "Обмен", icon: ArrowLeftRight, color: "#3B82F6" },
    EXCHANGE: { label: "Обмен", icon: ArrowLeftRight, color: "#3B82F6" }
  };

  const statusConfig = {
    COMPLETED: { label: "Выполнено", color: SBER.green, bg: SBER.greenPale },
    PENDING_PAYMENT: { label: "Ожидание", color: "#F59E0B", bg: "#FEF3C7" },
    PROCESSING: { label: "Обработка", color: "#3B82F6", bg: "#DBEAFE" },
    FAILED: { label: "Ошибка", color: "#EF4444", bg: "#FEE2E2" },
    CANCELED: { label: "Отменено", color: SBER.gray, bg: SBER.grayPale }
  };

  const filteredTxs = transactions.filter(tx => {
    if (filter === 'all') return true;
    return tx.status === filter;
  });

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-24"
      style={{ background: SBER.bg }}
    >
      {/* Header */}
      <div className="px-5 pt-6 pb-4" style={{ background: SBER.white }}>
        <h1 className="text-[22px] font-bold" style={{ color: SBER.dark }}>
          Транзакции
        </h1>
        <p className="text-[13px]" style={{ color: SBER.grayLight }}>
          Всего: {transactions.length}
        </p>
      </div>

      <div className="px-5 pt-4 space-y-4">
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { id: 'all', label: 'Все' },
            { id: 'COMPLETED', label: 'Выполнено' },
            { id: 'PENDING_PAYMENT', label: 'Ожидание' },
            { id: 'FAILED', label: 'Ошибки' }
          ].map((f) => (
            <motion.button
              key={f.id}
              whileTap={tapAnimation}
              onClick={() => setFilter(f.id)}
              className="px-4 py-2 rounded-full text-[13px] font-medium whitespace-nowrap transition-all"
              style={{
                background: filter === f.id ? SBER.green : SBER.white,
                color: filter === f.id ? SBER.white : SBER.gray
              }}
            >
              {f.label}
            </motion.button>
          ))}
        </div>

        {/* Transactions List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin" style={{ color: SBER.green }} />
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTxs.map((tx) => {
              const type = typeNames[tx.type] || { label: tx.type, icon: Activity, color: SBER.gray };
              const status = statusConfig[tx.status] || statusConfig.PENDING_PAYMENT;
              const TypeIcon = type.icon;
              
              return (
                <AppCard key={tx.id} className="p-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${type.color}20` }}
                    >
                      <TypeIcon className="w-5 h-5" style={{ color: type.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium" style={{ color: SBER.dark }}>
                        {type.label}
                      </p>
                      <p className="text-[12px]" style={{ color: SBER.grayLight }}>
                        {new Date(tx.created_at).toLocaleString('ru-RU', { 
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[14px]" style={{ color: SBER.dark, fontFamily: "'JetBrains Mono', monospace" }}>
                        {(tx.amount_rub || 0).toLocaleString()} ₽
                      </p>
                      <span 
                        className="text-[10px] px-2 py-0.5 rounded-full"
                        style={{ background: status.bg, color: status.color }}
                      >
                        {status.label}
                      </span>
                    </div>
                  </div>
                </AppCard>
              );
            })}
            {filteredTxs.length === 0 && (
              <div className="text-center py-8" style={{ color: SBER.grayLight }}>
                <ArrowLeftRight className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Транзакции не найдены</p>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Settings Page
const SettingsPage = () => {
  const [rate, setRate] = useState({ usdt_rub_rate: 95, buy_fee_percent: 1.7, sell_fee_percent: 2.0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await axios.get(`${API}/rate`);
        setRate(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchRate();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/rates`, rate);
      toast.success("Настройки сохранены");
      setEditMode(false);
    } catch (e) {
      toast.error("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const adjustRate = (delta) => {
    setRate(prev => ({ ...prev, usdt_rub_rate: Math.max(1, prev.usdt_rub_rate + delta) }));
  };

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-24"
      style={{ background: SBER.bg }}
    >
      {/* Header */}
      <div className="px-5 pt-6 pb-4" style={{ background: SBER.white }}>
        <h1 className="text-[22px] font-bold" style={{ color: SBER.dark }}>
          Настройки
        </h1>
        <p className="text-[13px]" style={{ color: SBER.grayLight }}>
          Управление курсом и комиссиями
        </p>
      </div>

      <div className="px-5 pt-4 space-y-4">
        {/* Rate Card */}
        <AppCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="font-semibold" style={{ color: SBER.dark }}>Курс USDT/RUB</p>
            <motion.button
              whileTap={tapAnimation}
              onClick={() => setEditMode(!editMode)}
              className="px-3 py-1.5 rounded-lg text-[13px] font-medium"
              style={{ background: SBER.greenPale, color: SBER.green }}
            >
              {editMode ? 'Отмена' : 'Изменить'}
            </motion.button>
          </div>

          <div className="flex items-center justify-center gap-4 py-4">
            {editMode && (
              <motion.button
                whileTap={tapAnimation}
                onClick={() => adjustRate(-1)}
                className="w-12 h-12 rounded-xl text-xl font-bold"
                style={{ background: SBER.grayPale, color: SBER.dark }}
              >
                −
              </motion.button>
            )}
            <div className="text-center">
              <p className="text-[36px] font-bold" style={{ color: SBER.green, fontFamily: "'JetBrains Mono', monospace" }}>
                {rate.usdt_rub_rate}
              </p>
              <p className="text-[13px]" style={{ color: SBER.grayLight }}>₽ за 1 USDT</p>
            </div>
            {editMode && (
              <motion.button
                whileTap={tapAnimation}
                onClick={() => adjustRate(1)}
                className="w-12 h-12 rounded-xl text-xl font-bold"
                style={{ background: SBER.grayPale, color: SBER.dark }}
              >
                +
              </motion.button>
            )}
          </div>

          {editMode && (
            <motion.button
              whileTap={tapAnimation}
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3 rounded-xl text-white font-semibold mt-4 flex items-center justify-center gap-2"
              style={{ background: SBER.green }}
            >
              {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Сохранить
            </motion.button>
          )}
        </AppCard>

        {/* Fees Card */}
        <AppCard className="p-5">
          <p className="font-semibold mb-4" style={{ color: SBER.dark }}>Комиссии</p>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-[13px]" style={{ color: SBER.gray }}>Покупка (RUB → USDT)</span>
                <span className="font-semibold" style={{ color: SBER.dark }}>{rate.buy_fee_percent}%</span>
              </div>
              <div className="h-2 rounded-full" style={{ background: SBER.grayPale }}>
                <div 
                  className="h-2 rounded-full"
                  style={{ width: `${rate.buy_fee_percent * 20}%`, background: SBER.green }}
                />
              </div>
            </div>
            
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-[13px]" style={{ color: SBER.gray }}>Продажа (USDT → RUB)</span>
                <span className="font-semibold" style={{ color: SBER.dark }}>{rate.sell_fee_percent}%</span>
              </div>
              <div className="h-2 rounded-full" style={{ background: SBER.grayPale }}>
                <div 
                  className="h-2 rounded-full"
                  style={{ width: `${rate.sell_fee_percent * 20}%`, background: '#F59E0B' }}
                />
              </div>
            </div>
          </div>
        </AppCard>

        {/* Info Card */}
        <AppCard className="p-4">
          <div className="flex items-start gap-3">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: '#DBEAFE' }}
            >
              <BarChart3 className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-[14px]" style={{ color: SBER.dark }}>Автоматический курс</p>
              <p className="text-[12px] mt-1" style={{ color: SBER.grayLight }}>
                Для автоматического обновления курса подключите XE Currency API в настройках сервера
              </p>
            </div>
          </div>
        </AppCard>
      </div>
    </motion.div>
  );
};

// Main Admin App - Role Based
import RoleBasedAdmin from "./components/admin/RoleBasedAdmin";

const AdminApp = () => {
  return <RoleBasedAdmin />;
};

export default AdminApp;
